import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  Editable,
  EditableInput,
  EditablePreview,
  HStack,
  VStack,
  Image,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiCopy, FiZoomIn, FiSave } from 'react-icons/fi';
import { projectApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useThumbnailCache } from '../contexts/ThumbnailCacheContext';
import ImagePreviewWithZoom from '../components/ImagePreviewWithZoom';
import PDFPreviewIframe from '../components/PDFPreviewIframe';
import ExportConfig from '../components/CsvExportConfig';

const OcrResult = () => {
  const { projectId, resultId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser } = useAuth();
  const { addToCache, getFromCache, hasInCache } = useThumbnailCache();
  
  // 特権管理権限があるかを判定
  const hasAdminPrivilege = currentUser && (currentUser.role === 'admin' || currentUser.role === 'privileged');
  const isPrivilegedUser = currentUser && currentUser.role === "privileged";
  
  const [result, setResult] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [multiTableData, setMultiTableData] = useState([]); // 複数テーブル用
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // タブ状態管理
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // キャッシュキー
  const cacheKey = `image_detail_${projectId}_${resultId}`;

  // 複数テーブル用のJSON解析関数
  const parseJsonToMultiTableData = (rawText) => {
    try {
      let jsonData = null;
      
      // まずマークダウンコードブロック内のJSONを抽出
      const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/g;
      const match = jsonBlockRegex.exec(rawText);
      
      if (match) {
        try {
          jsonData = JSON.parse(match[1].trim());
        } catch (parseError) {
          console.warn('JSONブロック解析エラー:', parseError);
        }
      }
      
      // マークダウンブロックが見つからない場合は、JSONオブジェクトを直接検索
      if (!jsonData) {
        const jsonMatch = rawText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          return null;
        }
        try {
          jsonData = JSON.parse(jsonMatch[0]);
        } catch (parseError) {
          return null;
        }
      }
      
      if (!jsonData || typeof jsonData !== 'object') {
        return null;
      }
      
      const tables = [];
      
      // ブロック名をキーとした構造を解析
      Object.keys(jsonData).forEach(blockName => {
        const blockValue = jsonData[blockName];
        
        if (Array.isArray(blockValue) && blockValue.length > 0) {
          // 配列データ → テーブル形式（テーブルブロック）
          const firstItem = blockValue[0];
          if (typeof firstItem === 'object' && firstItem !== null) {
            const columns = Object.keys(firstItem);
            const data = blockValue.map((item, index) => ({
              id: index,
              ...item,
              _editable: true
            }));
            
            tables.push({
              title: blockName,
              type: 'table_block',
              columns: columns,
              data: data,
              itemCount: blockValue.length
            });
          }
        } else if (typeof blockValue === 'object' && blockValue !== null) {
          // オブジェクトデータ → 1行テーブル（各キーがカラムになる）
          const keys = Object.keys(blockValue);
          const data = [{
            id: 0,
            ...blockValue,
            _editable: true
          }];
          
          tables.push({
            title: blockName,
            type: 'header_block',
            columns: keys,
            data: data,
            itemCount: 1
          });
        }
      });
      
      return tables;
    } catch (error) {
      console.error('複数テーブル解析エラー:', error);
      return null;
    }
  };

  // JSONデータを表形式データに変換する関数（既存との互換性維持）
  const parseJsonToTableData = (rawText) => {
    try {
      // まず複数テーブル形式で解析を試行
      const multiTables = parseJsonToMultiTableData(rawText);
      if (multiTables && multiTables.length > 0) {
        setMultiTableData(multiTables);
        
        // 最初のテーブルを従来形式でも設定（互換性のため）
        const firstTable = multiTables[0];
        setTableColumns(firstTable.columns);
        return firstTable.data;
      }
      
      // フォールバック：従来の解析方法
      const jsonMatch = rawText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return parseTextToTableData(rawText);
      }

      const jsonData = JSON.parse(jsonMatch[0]);
      
      if (jsonData.result && Array.isArray(jsonData.result)) {
        const results = jsonData.result;
        
        if (results.length === 0) {
          return [];
        }

        const columns = Object.keys(results[0]);
        setTableColumns(columns);

        const data = results.map((row, index) => ({
          id: index,
          ...row,
          _editable: true
        }));

        return data;
      } else {
        const columns = Object.keys(jsonData);
        setTableColumns(columns);
        
        const data = [{
          id: 0,
          ...jsonData,
          _editable: true
        }];

        return data;
      }
    } catch (error) {
      console.error('JSON解析エラー:', error);
      return parseTextToTableData(rawText);
    }
  };

  // テキストを行分割して表データに変換する関数（フォールバック）
  const parseTextToTableData = (rawText) => {
    const lines = rawText.split('\n').filter(line => line.trim());
    setTableColumns(['行番号', 'テキスト']);
    
    return lines.map((line, index) => ({
      id: index,
      '行番号': index + 1,
      'テキスト': line,
      _editable: true
    }));
  };
  
  // OCR結果の取得
  const fetchOcrResult = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectApi.getOcrResult(projectId, resultId);
      setResult(response.data);
      
      // JSONの構造を解析して表形式データを作成
      const parsedTableData = parseJsonToTableData(response.data.raw_text);
      setTableData(parsedTableData);
      
      // 前システムと同様に、OCR結果IDから正確に対応する画像ファイル名を導出
      // OCR結果IDは"20250423081003"や"20250423081003_raw.txt"のような形式
      // 対応する画像ファイル名は"20250423081003.jpg"のような形式

      // タイムスタンプ部分（接尾辞を除去）を抽出
      let imageTimestamp = resultId;
      if (imageTimestamp.includes('_')) {
        imageTimestamp = imageTimestamp.split('_')[0];
      }

      // タイムスタンプから実際のファイル名を見つけるためのロジック強化
      // OCR結果IDはタイムスタンプから作成されるため、
      // ファイル名はそのタイムスタンプと一致するか、それを含むはずです
      console.log("Extracting image timestamp for file:", imageTimestamp);
      
      // ファイル名から抽出したタイムスタンプを使用して、
      // OCR処理が行われたときにアップロードされた画像ファイルを特定します
      // これによって画像がタイムスタンプ部分とともに保存されていれば見つけられます
      
      // バックエンドからプロジェクト画像を取得するURLを構築
      const baseUrl = process.env.REACT_APP_API_URL || 'https://ocr-1.genxinc.ai';
      const imageUrl = `${baseUrl}/api/projects/${projectId}/images/${imageTimestamp}`;
      
      console.log("Image URL:", imageUrl);
      
      // ローカルストレージから認証トークンを取得
      const token = localStorage.getItem('auth_token');
      
      // PDFファイルを取得する関数
      const fetchPdfFile = async (resultData) => {
        try {
          console.log("===== PDFファイル取得開始 =====");
          console.log("Result object:", resultData);
          console.log("Original file name:", resultData?.original_file_name);
          console.log("File name:", resultData?.file_name);
          
          // PDFファイルの直接取得を試行（タイムスタンプベースのファイル名で）
          let imageTimestamp = resultId;
          if (imageTimestamp.includes('_')) {
            imageTimestamp = imageTimestamp.split('_')[0];
          }
          
          // baseURLが既に/api/を含まないことを確認
          const pdfCleanBaseUrl = baseUrl.endsWith('/api') ? 
            baseUrl.substring(0, baseUrl.length - 4) : 
            baseUrl;
          
          const pdfUrl = `${pdfCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp}`;
          console.log("PDF取得URL:", pdfUrl);
          console.log("Setting image src to:", pdfUrl);
          
          // 直接iframeでPDFを表示（ブラウザの標準機能を使用）
          setImageSrc(pdfUrl);
          setImageLoaded(true);
          setImageError(false);
          setPdfFile(null); // PDFPreviewIframeは使わずに直接iframe表示
          
          console.log("PDF設定完了 - imageSrc:", pdfUrl);
          
        } catch (error) {
          console.error("PDFファイル取得エラー:", error);
          setImageError(true);
        }
      };

      // 画像を取得する関数 - キャッシュを優先し、必要に応じて複数のURLを試行
      const fetchImage = async (resultData) => {
        try {
          console.log("===== 画像取得ロジック開始 =====");
          
          // PDFファイルかどうかを事前にチェック
          if (resultData && (
            (resultData.file_name && resultData.file_name.toLowerCase().endsWith('.pdf')) ||
            (resultData.original_file_name && resultData.original_file_name.toLowerCase().endsWith('.pdf'))
          )) {
            console.log("PDFファイルが検出されました。PDFファイルを取得します。");
            await fetchPdfFile(resultData);
            return;
          }
          
          // まずキャッシュを確認
          if (hasInCache(cacheKey)) {
            const cachedUrl = getFromCache(cacheKey);
            if (cachedUrl) {
              console.log(`画像詳細(${resultId}): キャッシュから読み込みました`, cachedUrl);
              setImageSrc(cachedUrl);
              setImageLoaded(true);
              setImageError(false);
              return;
            }
          }
          
          const baseUrl = process.env.REACT_APP_API_URL || 'https://ocr-1.genxinc.ai';
          
          // baseURLが既に/api/を含まないことを確認
          const imageCleanBaseUrl = baseUrl.endsWith('/api') ? 
            baseUrl.substring(0, baseUrl.length - 4) : 
            baseUrl;

          // バックエンドをデバッグするため、すべてのURLを詳細にログに残す
          console.log("画像取得の基本情報:");
          console.log(`- ベースURL: ${baseUrl}`);
          console.log(`- プロジェクトID: ${projectId}`);
          console.log(`- OCR結果ID: ${resultId}`);
          console.log(`- 抽出タイムスタンプ: ${imageTimestamp}`);

          // 特にタイムスタンプの抽出に問題がある可能性があるため、より詳細に分析
          console.log("タイムスタンプ分析:");
          if (resultId.includes('_')) {
            console.log(`- '_'を含むOCR結果ID: ${resultId}`);
            console.log(`- 分割後タイムスタンプ: ${resultId.split('_')[0]}`);
          }
          if (resultId.includes('.')) {
            console.log(`- '.'を含むOCR結果ID: ${resultId}`);
            console.log(`- 分割後タイムスタンプ: ${resultId.split('.')[0]}`);
          }

          // 画像URLの構築：複数のアクセス方法を準備
          console.log(`修正済みベースURL: ${imageCleanBaseUrl}`);
          
          const imageUrls = [
            // 0. 最優先: 拡張子なしでCORB対策専用エンドポイント (最も柔軟性が高い)
            `${imageCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp}`,
            
            // 1. 次に試す: CORB対策専用エンドポイントで一般的な拡張子
            `${imageCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp}.png`,
            `${imageCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp}.jpg`,
            `${imageCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp}.jpeg`,

            // 2. 次に: 日付部分のみで試す (タイムスタンプの先頭8桁 = YYYYMMDD)
            `${imageCleanBaseUrl}/api/direct-image/${projectId}/${imageTimestamp.substring(0, 8)}`,
            
            // 3. 完全パブリックな直接アクセス
            `${imageCleanBaseUrl}/api/public/projects/${projectId}/images/${imageTimestamp}`,
            `${imageCleanBaseUrl}/api/public/projects/${projectId}/images/${imageTimestamp}.png`,
            `${imageCleanBaseUrl}/api/public/projects/${projectId}/images/${imageTimestamp}.jpg`,

            // 4. バックアップ: 認証不要の公開エンドポイント
            `${imageCleanBaseUrl}/api/projects/${projectId}/public-image/${imageTimestamp}`,

            // 5. 直接静的ファイルアクセス (認証不要)
            `${imageCleanBaseUrl}/projects/${projectId}/images/${imageTimestamp}.png`,
            `${imageCleanBaseUrl}/projects/${projectId}/images/${imageTimestamp}.jpg`,

            // 6. 最後に試す: 認証付きAPIアクセス
            `${imageCleanBaseUrl}/api/projects/${projectId}/images/${imageTimestamp}`
          ];

          // すべてのURLをログに出力
          console.log("試行する画像URL一覧:");
          imageUrls.forEach((url, index) => {
            console.log(`${index + 1}. ${url}`);
          });

          // 最初のURLを設定
          setImageSrc(imageUrls[0]);
          console.log("初期URLを設定:", imageUrls[0]);

          // バックアップ方法を準備：最初の方法が失敗した場合
          let currentUrlIndex = 0;

          // エラー時に次のURLを試す関数
          const tryNextUrl = () => {
            currentUrlIndex++;

            if (currentUrlIndex < imageUrls.length) {
              console.log(`URL ${currentUrlIndex} が失敗。次を試行: ${imageUrls[currentUrlIndex]}`);

              if (currentUrlIndex === imageUrls.length - 1) {
                // 最後の手段: 認証付きAPIリクエスト
                console.log("最終手段: 認証付きAPIリクエストを試行中...");
                const token = localStorage.getItem('auth_token');

                // APIリクエストを実行
                axios({
                  method: 'GET',
                  url: imageUrls[currentUrlIndex],
                  responseType: 'arraybuffer',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'image/jpeg, image/png, image/*'
                  },
                  timeout: 10000
                })
                .then(response => {
                  if (response.data) {
                    const blob = new Blob([response.data], {
                      type: response.headers['content-type'] || 'image/jpeg'
                    });
                    const blobUrl = URL.createObjectURL(blob);

                    setImageSrc(blobUrl);
                    setImageLoaded(true);
                    setImageError(false);
                    console.log("API画像アクセス成功");
                    
                    // キャッシュに保存
                    addToCache(cacheKey, blobUrl);
                  }
                })
                .catch(apiError => {
                  console.error("API画像アクセス失敗:", apiError);
                  setImageError(true);
                });
              } else {
                // 通常のURL切り替え
                setImageSrc(imageUrls[currentUrlIndex]);
              }
              return true;
            }

            console.error("すべての画像URLが失敗しました");
            setImageError(true);
            return false;
          };

          // エラー処理のためのカスタム関数を定義
          window._handleImageError = () => {
            console.log(`画像読み込みエラー発生 (試行回数: ${window._imageRetryCount || 0})`);
            
            if (!window._imageRetryCount) {
              window._imageRetryCount = 0;
            }

            if (window._imageRetryCount < imageUrls.length - 1) {
              window._imageRetryCount++;
              console.log(`次のURLを試行 (${window._imageRetryCount}/${imageUrls.length-1})`);
              return tryNextUrl();
            }
            
            console.log("すべてのURLが失敗しました。表示を中止します。");
            return false;
          };

        } catch (error) {
          console.error("画像取得セットアップ失敗:", error);
          setImageError(true);
        }
      };
      
      // 画像取得を実行（response.dataを渡す）
      fetchImage(response.data);
      
    } catch (error) {
      console.error('Failed to fetch OCR result:', error);
      setError('OCR結果の取得に失敗しました');
      
      if (error.response?.status === 404) {
        toast({
          title: '結果が見つかりません',
          description: '指定されたOCR結果が存在しないか、アクセス権限がありません。',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate(`/project/${projectId}`);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // コンポーネントのマウント時にOCR結果を取得
  useEffect(() => {
    fetchOcrResult();
  }, [projectId, resultId]);
  
  // テーブルデータの更新
  const handleTableCellUpdate = (rowId, column, newValue) => {
    setTableData(prevData =>
      prevData.map(row => 
        row.id === rowId ? { ...row, [column]: newValue } : row
      )
    );
  };

  // 複数テーブルのセル更新
  const handleMultiTableCellUpdate = (tableIndex, rowId, column, newValue) => {
    setMultiTableData(prevData =>
      prevData.map((tableInfo, index) => 
        index === tableIndex ? {
          ...tableInfo,
          data: tableInfo.data.map(row => 
            row.id === rowId ? { ...row, [column]: newValue } : row
          )
        } : tableInfo
      )
    );
  };
  
  // 更新されたテーブルデータを保存
  const handleSaveTableData = () => {
    // ここでバックエンドに更新データを送信する
    // 現状ではバックエンドAPIが実装されていないため、トーストだけ表示
    toast({
      title: '更新が保存されました',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };
  
  // クリップボードにコピー
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'クリップボードにコピーしました',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  // CSVダウンロード
  const handleDownloadCsv = () => {
    if (tableData.length === 0 || tableColumns.length === 0) {
      toast({
        title: 'エラー',
        description: 'ダウンロード可能なデータがありません',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // ヘッダー行を作成
    const headers = tableColumns.join(',');
    
    // データ行を作成
    const rows = tableData.map(row => {
      return tableColumns.map(column => {
        const value = row[column] || '';
        // CSVエスケープ処理
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });
    
    // CSVコンテンツを作成
    const csvContent = [headers, ...rows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ocr_result_${resultId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: 'ダウンロード完了',
      description: 'CSVファイルをダウンロードしました',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // 複数テーブル対応のデータ出力
  const handleMultiTableDataExport = (enabledTables, outputMode = 'flat', outputFormat = 'csv', tableSelections = {}) => {
    if (!enabledTables || enabledTables.length === 0) {
      toast({
        title: 'エラー',
        description: '出力するテーブルが選択されていません',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // まずJSONデータを作成（統一データ形式）
      let unifiedData = [];
      
      if (outputMode === 'flat') {
        // フラット形式：反復設定に基づくデータマージ
        const repeatTables = enabledTables.filter((table, index) => {
          const tableConfig = Object.values(tableSelections)[index];
          return tableConfig?.repeatToAllRows === true;
        });
        
        const nonRepeatTables = enabledTables.filter((table, index) => {
          const tableConfig = Object.values(tableSelections)[index];
          return tableConfig?.repeatToAllRows !== true;
        });

        // 反復するテーブルからデータを抽出
        const repeatData = {};
        repeatTables.forEach(table => {
          table.data.forEach(row => {
            table.columns.forEach(col => {
              const key = col.custom;
              const value = row[col.original] || '';
              
              // 直接データを設定（新しい構造では各キーが直接カラムになる）
              repeatData[key] = value;
            });
          });
        });

        // すべてのカラムを収集
        const allColumns = ['id'];
        repeatTables.forEach(table => {
          table.columns.forEach(col => {
            if (!allColumns.includes(col.custom)) {
              allColumns.push(col.custom);
            }
          });
        });
        nonRepeatTables.forEach(table => {
          table.columns.forEach(col => {
            if (!allColumns.includes(col.custom)) {
              allColumns.push(col.custom);
            }
          });
        });

        // 行データを作成（非反復テーブルの行数に基づく）
        let maxRows = 1;
        nonRepeatTables.forEach(table => {
          if (table.data.length > maxRows) {
            maxRows = table.data.length;
          }
        });

        for (let i = 0; i < maxRows; i++) {
          const rowData = { id: i + 1 };
          
          // 反復データを追加
          Object.keys(repeatData).forEach(key => {
            if (allColumns.includes(key)) {
              rowData[key] = repeatData[key];
            }
          });
          
          // 非反復データを追加
          nonRepeatTables.forEach(table => {
            if (table.data[i]) {
              table.columns.forEach(col => {
                rowData[col.custom] = table.data[i][col.original] || '';
              });
            }
          });
          
          unifiedData.push(rowData);
        }
      } else {
        // テーブル分離形式
        unifiedData = {
          tables: enabledTables.map(table => ({
            name: table.tableName,
            columns: table.columns.map(col => col.custom),
            data: table.data.map(row => {
              const rowData = {};
              table.columns.forEach(col => {
                rowData[col.custom] = row[col.original] || '';
              });
              return rowData;
            })
          }))
        };
      }

      // 出力形式に応じてダウンロード
      if (outputFormat === 'json') {
        // JSON出力
        const jsonContent = JSON.stringify(unifiedData, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `ocr_${outputMode}_${resultId}.json`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // CSV出力
        let csvContent = '';
        
        if (outputMode === 'flat') {
          // フラット形式のCSV
          if (unifiedData.length > 0) {
            const headers = Object.keys(unifiedData[0]);
            csvContent = headers.join(',') + '\n';
            
            unifiedData.forEach(row => {
              const csvRow = headers.map(header => {
                const value = row[header] || '';
                // CSVエスケープ処理
                if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              });
              csvContent += csvRow.join(',') + '\n';
            });
          }
        } else {
          // テーブル分離形式のCSV
          if (unifiedData.tables) {
            unifiedData.tables.forEach((table, tableIndex) => {
              if (tableIndex > 0) {
                csvContent += '\n\n'; // テーブル間の区切り
              }
              csvContent += `# ${table.name}\n`;
              
              // ヘッダー行を作成
              csvContent += table.columns.join(',') + '\n';
              
              // データ行を作成
              table.data.forEach(row => {
                const csvRow = table.columns.map(column => {
                  const value = row[column] || '';
                  // CSVエスケープ処理
                  if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                  }
                  return value;
                });
                csvContent += csvRow.join(',') + '\n';
              });
            });
          }
        }

        // CSVファイルとしてダウンロード
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `ocr_${outputMode}_${resultId}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast({
        title: `${outputFormat.toUpperCase()}出力完了`,
        description: `${outputMode === 'flat' ? 'フラット形式' : 'テーブル分離形式'}で${outputFormat.toUpperCase()}ファイルを出力しました`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('データ出力エラー:', error);
      toast({
        title: 'エラー',
        description: `${outputFormat.toUpperCase()}出力に失敗しました`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // ローディング中
  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </Flex>
    );
  }
  
  // エラー時
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }
  
  // 結果が存在しない場合
  if (!result) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        OCR結果が見つかりません
      </Alert>
    );
  }
  
  return (
    <Box>
      {/* ヘッダー */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        bg={useColorModeValue('white', 'gray.800')}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
      >
        <HStack>
          <Button
            leftIcon={<FiArrowLeft />}
            variant="ghost"
            onClick={() => navigate(`/project/${projectId}`)}
          >
            プロジェクトに戻る
          </Button>
          <Heading size="md">OCR結果 #{resultId}</Heading>
        </HStack>
        
        <HStack spacing={3}>
          {/* 元のファイル名表示を追加 */}
          {result.original_file_name && (
            <Badge colorScheme="purple">
              元ファイル: {result.original_file_name}
            </Badge>
          )}
          {/* AIプロバイダー情報（特権管理権限ユーザーのみ） */}
          {hasAdminPrivilege && result.ai_provider && (
            <Badge 
              colorScheme={
                result.ai_provider === 'openai' ? 'green' :
                result.ai_provider === 'claude' ? 'purple' :
                result.ai_provider === 'mistral' ? 'orange' :
                result.ai_provider === 'gemini' ? 'blue' :
                'gray'
              }
            >
              AI: {result.ai_provider === 'openai' ? 'OpenAI' :
                   result.ai_provider === 'claude' ? 'Claude' :
                   result.ai_provider === 'mistral' ? 'Mistral' :
                   result.ai_provider === 'gemini' ? 'Gemini' :
                   result.ai_provider.toUpperCase()}
            </Badge>
          )}
          <Badge colorScheme="blue">
            {new Date(resultId.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
          </Badge>
          {/* 旧CSV機能は非表示にして、新しいCSV設定機能のみ使用 */}
          {/* 
          <Tooltip label="CSVでダウンロード">
            <IconButton
              icon={<FiDownload />}
              onClick={handleDownloadCsv}
              aria-label="ダウンロード"
            />
          </Tooltip>
          */}
        </HStack>
      </Flex>
      
      {/* メインコンテンツ */}
      <Grid templateColumns={{ base: "1fr", lg: "500px 1fr" }} gap={6}>
        {/* 左側: 元画像 */}
        <GridItem
          bg={useColorModeValue('white', 'gray.800')}
          p={4}
          borderRadius="lg"
          boxShadow="sm"
          position="relative"
          minWidth={{ lg: "500px" }}
        >
          <Flex direction="column" h="100%">
            <Heading size="sm" mb={4}>元画像</Heading>
            <Box 
              position="relative" 
              flex="1" 
              overflow="hidden"
              borderRadius="md"
              bg="gray.100"
              height={{ base: "400px", lg: "600px" }}
              minHeight="400px"
            >
              {imageSrc && !imageError ? (
                result && (
                  (result.file_name && result.file_name.toLowerCase().endsWith('.pdf')) ||
                  (result.original_file_name && result.original_file_name.toLowerCase().endsWith('.pdf'))
                ) ? (
                  // PDFファイルの場合はobjectタグで表示
                  <object
                    data={imageSrc}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  >
                    <embed
                      src={imageSrc}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                    />
                    <p>PDFを表示できません。<a href={imageSrc} target="_blank" rel="noopener noreferrer">こちらをクリック</a>してダウンロードしてください。</p>
                  </object>
                ) : (
                  // 画像ファイルの場合は拡大縮小可能なプレビュー表示
                  <ImagePreviewWithZoom
                    src={imageSrc}
                    alt="OCR処理された画像"
                    onLoad={() => {
                      console.log("ImagePreviewWithZoom: 画像読み込み完了");
                      setImageLoaded(true);
                      setImageError(false);
                    }}
                    onError={(e) => {
                      console.log("ImagePreviewWithZoom: 画像読み込みエラー", e);
                      if (window._handleImageError && window._handleImageError()) {
                        setImageError(false);
                      } else {
                        setImageError(true);
                      }
                    }}
                  />
                )
              ) : imageError ? (
                <Flex 
                  height="100%" 
                  alignItems="center" 
                  justifyContent="center"
                  bg="gray.100"
                  borderRadius="md"
                  direction="column"
                  p={6}
                  textAlign="center"
                >
                  <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.700">元ファイル</Text>
                  {result && (
                    <Box mb={4}>
                      <Text fontSize="sm" color="gray.600" mb={2}>ファイル名:</Text>
                      <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.800">
                        {result.original_file_name || result.file_name}
                      </Text>
                      
                      {result.file_name && result.file_name.toLowerCase().endsWith('.pdf') ? (
                        <Badge colorScheme="red" fontSize="sm" px={3} py={1}>
                          PDFファイル
                        </Badge>
                      ) : (
                        <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                          画像ファイル
                        </Badge>
                      )}
                    </Box>
                  )}
                  <Text fontSize="sm" color="gray.500">
                    {result && result.file_name && result.file_name.toLowerCase().endsWith('.pdf') 
                      ? 'PDFファイルが正常に処理されました。OCR結果は右側に表示されています。'
                      : '画像プレビューの読み込みに失敗しました'
                    }
                  </Text>
                </Flex>
              ) : (
                <Flex 
                  height="100%" 
                  alignItems="center" 
                  justifyContent="center"
                  bg="gray.100"
                  borderRadius="md"
                >
                  <Spinner size="lg" />
                  <Text ml={3} color="gray.500">画像を読み込み中...</Text>
                </Flex>
              )}
            </Box>
          </Flex>
        </GridItem>
        
        {/* 右側: OCR結果 */}
        <GridItem
          bg={useColorModeValue('white', 'gray.800')}
          p={4}
          borderRadius="lg"
          boxShadow="sm"
        >
          <Tabs 
            variant="enclosed" 
            colorScheme="brand"
            index={activeTabIndex}
            onChange={(index) => setActiveTabIndex(index)}
          >
            <TabList>
              <Tab>表形式</Tab>
              <Tab>生データ</Tab>
              {isPrivilegedUser && <Tab>使用したプロンプト</Tab>}
            </TabList>
            
            <TabPanels>
              {/* 表形式タブ */}
              <TabPanel p={0} pt={4}>
                <Flex direction="column" h="100%">
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm">
                      テキスト（表形式）
                      {multiTableData.length > 1 && (
                        <Badge ml={2} colorScheme="brand">
                          {multiTableData.length}個のテーブル
                        </Badge>
                      )}
                    </Heading>
                    <Button
                      size="sm"
                      leftIcon={<FiSave />}
                      colorScheme="brand"
                      onClick={handleSaveTableData}
                    >
                      変更を保存
                    </Button>
                  </Flex>
                  
                  {/* 複数テーブル表示または従来表示 */}
                  {multiTableData.length > 0 ? (
                    <VStack spacing={6} align="stretch" overflowY="auto" maxH="600px">
                      {multiTableData.map((tableInfo, tableIndex) => (
                        <Box key={tableIndex}>
                          <Flex justify="space-between" align="center" mb={3}>
                            <Heading size="md" color="brand.500">
                              ■ {tableInfo.title}
                              <Badge ml={2} colorScheme="gray" fontSize="xs">
                                {tableInfo.itemCount}件
                              </Badge>
                            </Heading>
                          </Flex>
                          
                          <Box 
                            border="1px solid" 
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            borderRadius="md"
                            overflow="hidden"
                          >
                            <Table variant="simple" size="sm">
                              <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
                                <Tr>
                                  {tableInfo.type === 'table_block' && <Th width="60px">行</Th>}
                                  {tableInfo.columns.map(column => (
                                    <Th key={column}>{column}</Th>
                                  ))}
                                </Tr>
                              </Thead>
                              <Tbody>
                                {tableInfo.data.map(row => (
                                  <Tr key={row.id}>
                                    {tableInfo.type === 'table_block' && <Td>{row.id + 1}</Td>}
                                    {tableInfo.columns.map(column => {
                                      const cellValue = row[column];
                                      const displayValue = typeof cellValue === 'object' && cellValue !== null 
                                        ? JSON.stringify(cellValue, null, 2) 
                                        : cellValue || '';
                                      
                                      return (
                                        <Td key={column} verticalAlign="top">
                                          <Editable defaultValue={String(displayValue)} width="100%">
                                            <EditablePreview width="100%" whiteSpace="pre-wrap" />
                                            <EditableInput
                                              width="100%"
                                              onChange={(e) => handleMultiTableCellUpdate(tableIndex, row.id, column, e.target.value)}
                                            />
                                          </Editable>
                                        </Td>
                                      );
                                    })}
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Box overflowY="auto" maxH="600px">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th width="60px">行</Th>
                            {tableColumns.map(column => (
                              <Th key={column}>{column}</Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {tableData.map(row => (
                            <Tr key={row.id}>
                              <Td>{row.id + 1}</Td>
                              {tableColumns.map(column => {
                                // セル値がオブジェクトの場合はJSONに変換
                                const cellValue = row[column];
                                const displayValue = typeof cellValue === 'object' && cellValue !== null 
                                  ? JSON.stringify(cellValue, null, 2) 
                                  : cellValue || '';
                                
                                return (
                                  <Td key={column}>
                                    <Editable defaultValue={String(displayValue)} width="100%">
                                      <EditablePreview width="100%" />
                                      <EditableInput
                                        width="100%"
                                        onChange={(e) => handleTableCellUpdate(row.id, column, e.target.value)}
                                      />
                                    </Editable>
                                  </Td>
                                );
                              })}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                  
                  {/* 出力設定コンポーネント */}
                  <ExportConfig
                    multiTableData={multiTableData}
                    onExportCsv={handleMultiTableDataExport}
                    projectId={projectId}
                    resultId={resultId}
                  />
                </Flex>
              </TabPanel>
              
              {/* 生データタブ */}
              <TabPanel p={0} pt={4}>
                <Flex direction="column" h="100%">
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm">テキスト（生データ）</Heading>
                    <Tooltip label="クリップボードにコピー">
                      <IconButton
                        icon={<FiCopy />}
                        onClick={() => handleCopyToClipboard(result.raw_text)}
                        aria-label="コピー"
                      />
                    </Tooltip>
                  </Flex>
                  
                  <Textarea
                    value={result.raw_text}
                    readOnly
                    minH="600px"
                    maxH="600px"
                    overflowY="auto"
                    whiteSpace="pre-wrap"
                    fontFamily="monospace"
                  />
                </Flex>
              </TabPanel>
              
              {/* 使用したプロンプトタブ - 特権ユーザーのみ表示 */}
              {isPrivilegedUser && (
                <TabPanel p={0} pt={4}>
                  <Flex direction="column" h="100%">
                    <Flex justify="space-between" align="center" mb={4}>
                      <Heading size="sm">使用したプロンプト</Heading>
                      <Tooltip label="クリップボードにコピー">
                        <IconButton
                          icon={<FiCopy />}
                          onClick={() => handleCopyToClipboard(result.used_prompt || "プロンプト情報がありません")}
                          aria-label="コピー"
                        />
                      </Tooltip>
                    </Flex>
                    
                    <Textarea
                      value={result.used_prompt || "プロンプト情報がありません"}
                      readOnly
                      minH="600px"
                      maxH="600px"
                      overflowY="auto"
                      whiteSpace="pre-wrap"
                      fontFamily="monospace"
                      placeholder="このOCR処理で使用されたプロンプトがここに表示されます"
                    />
                  </Flex>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
      
      {/* 画像拡大モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>画像拡大表示</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" justifyContent="center" alignItems="center">
            <Image
              src={imageSrc}
              alt="OCR処理された画像"
              maxH="90vh"
              objectFit="contain"
              onError={(e) => {
                console.error("モーダル内画像の読み込みに失敗しました", e);

                console.log("===== モーダル内画像読み込みエラー発生 =====");
                const baseUrl = process.env.REACT_APP_API_URL || 'https://ocr-1.genxinc.ai';
                
                // baseURLが既に/api/を含まないことを確認
                const modalCleanBaseUrl = baseUrl.endsWith('/api') ? 
                  baseUrl.substring(0, baseUrl.length - 4) : 
                  baseUrl;

                // タイムスタンプ部分を抽出 (モーダル内で再度定義)
                let modalImageTimestamp = resultId;
                if (modalImageTimestamp.includes('_')) {
                  modalImageTimestamp = modalImageTimestamp.split('_')[0];
                  console.log(`モーダル: '_'を含むOCR結果ID: ${resultId} → ${modalImageTimestamp}`);
                }
                if (modalImageTimestamp.includes('.')) {
                  modalImageTimestamp = modalImageTimestamp.split('.')[0];
                  console.log(`モーダル: '.'を含むOCR結果ID: ${resultId} → ${modalImageTimestamp}`);
                }

                // モーダル専用のリトライカウンター
                if (!window._modalRetryCount) {
                  window._modalRetryCount = 0;
                } else {
                  window._modalRetryCount++;
                }
                
                console.log(`モーダル: リトライ回数 ${window._modalRetryCount}`);

                // 最大10回まで試行
                if (window._modalRetryCount < 10) {
                  console.log(`モーダル: 修正済みベースURL: ${modalCleanBaseUrl}`);
                  
                  // メイン画面と同じような、多様なURLフォーマットを試す
                  const imageUrls = [
                    // 0. 最優先: 拡張子なしでCORB対策専用エンドポイント
                    `${modalCleanBaseUrl}/api/direct-image/${projectId}/${modalImageTimestamp}`,
                    
                    // 1. CORB対策専用エンドポイント (一般的な拡張子)
                    `${modalCleanBaseUrl}/api/direct-image/${projectId}/${modalImageTimestamp}.png`,
                    `${modalCleanBaseUrl}/api/direct-image/${projectId}/${modalImageTimestamp}.jpg`,
                    `${modalCleanBaseUrl}/api/direct-image/${projectId}/${modalImageTimestamp}.jpeg`,
                    
                    // 2. 日付部分のみで試す (タイムスタンプの先頭8桁 = YYYYMMDD)
                    `${modalCleanBaseUrl}/api/direct-image/${projectId}/${modalImageTimestamp.substring(0, 8)}`,
                    
                    // 3. パブリック直接アクセス
                    `${modalCleanBaseUrl}/api/public/projects/${projectId}/images/${modalImageTimestamp}`,
                    `${modalCleanBaseUrl}/api/public/projects/${projectId}/images/${modalImageTimestamp}.png`,
                    `${modalCleanBaseUrl}/api/public/projects/${projectId}/images/${modalImageTimestamp}.jpg`,
                    
                    // 4. 公開エンドポイント
                    `${modalCleanBaseUrl}/api/projects/${projectId}/public-image/${modalImageTimestamp}`,
                    
                    // 5. 静的ファイルアクセス
                    `${modalCleanBaseUrl}/projects/${projectId}/images/${modalImageTimestamp}.png`,
                    `${modalCleanBaseUrl}/projects/${projectId}/images/${modalImageTimestamp}.jpg`
                  ];

                  // 順番に試行する (リトライカウントをインデックスとして使用)
                  const nextUrlIndex = window._modalRetryCount % imageUrls.length;
                  const nextUrl = imageUrls[nextUrlIndex];

                  console.log(`モーダル: 次のURL試行 (${window._modalRetryCount}/${imageUrls.length}): ${nextUrl}`);
                  e.target.src = nextUrl;
                  return;
                }
                
                console.log("モーダル: すべてのURL試行が失敗しました");

                // すべての試行が失敗した場合
                e.target.onerror = null; // 無限ループを防止
                e.target.src = `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23f0f0f0" width="400" height="300"/><text fill="rgba(0,0,0,0.5)" font-family="sans-serif" font-size="18" dy="10.5" font-weight="bold" x="50%" y="50%" text-anchor="middle">画像が見つかりません</text></svg>`;
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OcrResult;